import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Brand";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Brand /></div>
        {!token ? (
          <div data-testid="reset-invalid">
            <h1 className="font-display text-2xl font-bold text-slate-50">Invalid reset link</h1>
            <p className="text-sm text-slate-400 mt-2">This link is missing its token. Please request a new password reset.</p>
            <Link to="/login" className="text-primary hover:underline text-sm mt-4 inline-block">Back to sign in</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-3" />
            <h1 className="font-display text-2xl font-bold text-slate-50">Password reset</h1>
            <p className="text-sm text-slate-400 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-50">Set a new password</h1>
            <p className="text-sm text-slate-400 mt-1 mb-6">Choose a strong password you don't use elsewhere.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs">New password</Label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="reset-password-input" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Confirm password</Label>
                <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="reset-confirm-input" />
              </div>
              {error && <p className="text-xs text-red-400" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-emerald-600 text-white" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Reset password <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
            <Link to="/login" className="text-primary hover:underline text-sm mt-6 inline-block">Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
